import { ButtonCus } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Textarea } from '@/components/common/Textarea';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';
import { ADDITIONAL_REQUEST_PAYMENT_TYPE, REFUND_STATUS_DATA, SORT_ADDITIONAL_REQUEST_PAYMENT_TYPE } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { ADDITIONAL_PAYMENT_TYPE } from '@/types/enums';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface UpdateCancelOrderProps {
    id: number,
    reload?: Function
}
export default observer(function PaymentRequestAries({ id, reload }: UpdateCancelOrderProps) {
    // hook
    const { t } = useTranslation();

    // store
    const { reservationStore: { requestAdditionalPayment }, modalStore: { hideAllModals, showAlertModal } } = useStore();
    // validate schema
    const validateSchema = yup.object().shape({
        additionalType: yup.string().required('form_error_validate.required'),
        kilometers: yup
            .string().trim()
            .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
            .when('additionalType', {
                is: value => [ADDITIONAL_PAYMENT_TYPE.OVER_MILEAGE.valueOf(), ADDITIONAL_PAYMENT_TYPE.DROP_OFF.valueOf()].includes(Number(value)),
                then: schema => schema.required('form_error_validate.required').test(
                    'min-value',
                    t('form_error_validate.min', { min: 1 }),
                    (val) => {
                        if (val == null || val === '') {
                            return true;
                        }
                        const num = Number(val);
                        return num >= 1;
                    }
                ),
                otherwise: schema => schema.optional()
            }),
        amount: yup
            .string().trim()
            .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
            .required('form_error_validate.required')
            .test(
                'min-value-amount',
                t('form_error_validate.min', { min: 1 }),
                (val) => {
                    if (val == null || val === '') {
                        return true;
                    }
                    const num = Number(val);
                    return num >= 1;
                }
            ),
        note: yup.string().trim().required('form_error_validate.required'),
        isNotSendOwner: yup.boolean()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            additionalType: ADDITIONAL_PAYMENT_TYPE.TACHIGOKE.toString(),
            kilometers: undefined,
            amount: undefined,
            note: '',
            isNotSendOwner: false
        }
    });

    const watchAdditionalType = form.watch('additionalType');

    // lifecycle
    useEffect(() => {
        if (![ADDITIONAL_PAYMENT_TYPE.DROP_OFF.valueOf(), ADDITIONAL_PAYMENT_TYPE.OVER_MILEAGE.valueOf()].includes(Number(watchAdditionalType))) {
            form.setValue('kilometers', undefined);
        }
    }, [watchAdditionalType]);

    // function
    const onSubmit = async (data: FormData) => {
        let payload = {
            orderId: id,
            ...data
        };
        const result = await flowResult(requestAdditionalPayment(payload));
        if (result) {
            hideAllModals();
            showAlertModal({
                id: 'update-success',
                type: 'success',
                content: t('messages.send_success'),
                onClickXIcon: () => {
                    reload && reload();
                }
            });
            // reload && reload();
        }
    };

    const AdditionnalPaymentSatusList = SORT_ADDITIONAL_REQUEST_PAYMENT_TYPE.map(key => ({
        label: t(ADDITIONAL_REQUEST_PAYMENT_TYPE[key].key),
        value: ADDITIONAL_REQUEST_PAYMENT_TYPE[key]?.value.toString(),
        code: REFUND_STATUS_DATA[key]?.value.toString()
    }));

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div>
                    <FormField
                        control={form.control}
                        name='additionalType'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('field.payment_type')}</FormLabel>
                                <FormControl>
                                    <Combobox
                                        search={false}
                                        value={field?.value?.toString() ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.value);
                                        }}
                                        disableClearIcon
                                        items={
                                            AdditionnalPaymentSatusList
                                        }
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                {
                    [ADDITIONAL_PAYMENT_TYPE.OVER_MILEAGE.valueOf(), ADDITIONAL_PAYMENT_TYPE.DROP_OFF.valueOf()].includes(Number(watchAdditionalType)) && (
                        <div className='mt-4'>
                            <FormField
                                control={form.control}
                                name='kilometers'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{Number(watchAdditionalType) === ADDITIONAL_PAYMENT_TYPE.OVER_MILEAGE ? t('field.increased_kilometers') : t('field.increased_kilometers_dropoff')}</FormLabel>
                                        <FormControl>
                                            <FormatNumberVer2
                                                suffix='km'
                                                decimalScale={0}
                                                placeholder={t('placeholder.enter')}
                                                statusDisplay='input'
                                                thousandSeparator={true}
                                                allowNegative={false}
                                                value={field.value}
                                                setvalueOfNumericFormat={field.onChange}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    )
                }
                <div className='mt-4'>
                    <FormField
                        control={form.control}
                        name='amount'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('field.additional_payment_amount')}</FormLabel>
                                <FormControl>
                                    <FormatNumberVer2
                                        prefix={t('currency.currency_symbol')}
                                        decimalScale={0}
                                        placeholder={t('placeholder.enter')}
                                        statusDisplay='input'
                                        thousandSeparator={true}
                                        allowNegative={false}
                                        value={field.value}
                                        maxLength={10}
                                        setvalueOfNumericFormat={field.onChange}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='mt-4'>
                    <FormField
                        control={form.control}
                        name='note'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('words_title.comment')}</FormLabel>
                                <FormControl>
                                    <Textarea {...field} rows={5} maxLength={500} placeholder={t('placeholder.enter')}></Textarea>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='mt-4'>
                    <FormField
                        control={form.control}
                        name='isNotSendOwner'
                        render={({ field }) => (
                            <FormItem className='flex items-center'>
                                <FormControl>
                                    <Checkbox
                                        checked={field?.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormControl>
                                <FormLabel className='font-normal cursor-pointer mb-0 ml-1.5'>
                                    {t('sentences.not_send_mail_to_owner')}
                                </FormLabel>
                            </FormItem>
                        )}
                    />
                </div>
                <div>
                    <div className='flex justify-center mt-8'>
                        <ButtonCus variant='default' size='sm' type='submit'>{t('button.send')}</ButtonCus>
                    </div>
                </div>
            </form>
        </Form>
    );
});
